import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { Clock, FileText, PenTool, CheckCircle2, PlayCircle, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface WorkflowInstance {
  id: string;
  workflow_id: string;
  task_id: string;
  workflow_title: string;
  workflow_description: string;
  status: string;
  current_phase: number;
  total_phases: number;
  progress_percentage: number;
  started_at: string;
  completed_at: string | null;
  document_count: number;
  signature_count: number;
  icon_name: string;
}

interface MyWorkflowsTabProps {
  workflows: WorkflowInstance[];
  loading: boolean;
  onOpenWorkflow: (workflow: WorkflowInstance) => void;
  onDeleteWorkflow: (workflow: WorkflowInstance) => void;
  highlightedWorkflowId?: string | null;
}

export const MyWorkflowsTab = ({ workflows, loading, onOpenWorkflow, onDeleteWorkflow, highlightedWorkflowId }: MyWorkflowsTabProps) => {
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<WorkflowInstance | null>(null);
  const [showAllCompleted, setShowAllCompleted] = useState(false);
  
  const inProgressWorkflows = workflows.filter(w => w.status !== 'completed' && w.status !== 'abandoned');
  const completedWorkflows = workflows.filter(w => w.status === 'completed');

  const handleDeleteClick = (e: React.MouseEvent, workflow: WorkflowInstance) => {
    e.stopPropagation();
    setWorkflowToDelete(workflow);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (workflowToDelete) {
      onDeleteWorkflow(workflowToDelete);
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_progress':
        return <Badge className="bg-blue-500/10 text-blue-500"><PlayCircle className="h-3 w-3 mr-1" />In Progress</Badge>;
      case 'completed':
        return <Badge className="bg-green-500/10 text-green-500"><CheckCircle2 className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'abandoned':
        return <Badge variant="secondary">Abandoned</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const renderWorkflowCard = (workflow: WorkflowInstance) => (
    <Card
      key={workflow.id}
      className={cn(
        "hover:shadow-lg transition-all cursor-pointer relative group",
        highlightedWorkflowId === workflow.workflow_id && "ring-2 ring-primary animate-pulse"
      )}
      onClick={() => onOpenWorkflow(workflow)}
    >
      <CardHeader>
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <CardTitle className="text-lg">{workflow.workflow_title}</CardTitle>
              {getStatusBadge(workflow.status)}
            </div>
            <CardDescription className="line-clamp-2">
              {workflow.workflow_description}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{workflow.progress_percentage}%</span>
          </div>
          <Progress value={workflow.progress_percentage} className="h-2" />
        </div>

        {/* Phase Progress */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Phase {workflow.current_phase} of {workflow.total_phases}</span>
          </div>
          <span className="text-muted-foreground">
            Started {format(new Date(workflow.started_at), "MMM d, yyyy")}
          </span>
        </div>

        {/* Documents & Signatures */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <FileText className="h-4 w-4" />
            <span>{workflow.document_count} docs</span>
          </div>
          <div className="flex items-center gap-1">
            <PenTool className="h-4 w-4" />
            <span>{workflow.signature_count} signatures</span>
          </div>
        </div>
      </CardContent>
      
      {/* Delete Button */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute bottom-3 right-3 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity bg-background/80 hover:bg-destructive/10 hover:text-destructive"
        onClick={(e) => handleDeleteClick(e, workflow)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </Card>
  );

  const renderCompletedWorkflowRow = (workflow: WorkflowInstance) => (
    <div
      key={workflow.id}
      className="flex items-center justify-between py-3 px-4 border-b last:border-b-0 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={() => onOpenWorkflow(workflow)}
    >
      <div className="flex items-center gap-3">
        <CheckCircle2 className="h-4 w-4 text-green-500" />
        <span className="font-medium">{workflow.workflow_title}</span>
      </div>
      <div className="flex items-center gap-6 text-sm text-muted-foreground">
        <div className="flex items-center gap-1">
          <FileText className="h-4 w-4" />
          <span>{workflow.document_count} docs</span>
        </div>
        <span className="min-w-[100px] text-right">
          Completed {workflow.completed_at 
            ? format(new Date(workflow.completed_at), "MMM d, yyyy") 
            : format(new Date(workflow.started_at), "MMM d, yyyy")}
        </span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your workflows...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* In Progress Section */}
      <div>
        <h3 className="text-lg font-semibold mb-4">In Progress ({inProgressWorkflows.length})</h3>
        {inProgressWorkflows.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground mb-4">No workflows in progress</p>
            <p className="text-sm text-muted-foreground">
              Launch a workflow from the Available Workflows tab to get started
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {inProgressWorkflows.map(renderWorkflowCard)}
          </div>
        )}
      </div>

      {/* Completed Section */}
      {completedWorkflows.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Completed ({completedWorkflows.length})</h3>
          <Card className="divide-y">
            {(showAllCompleted ? completedWorkflows : completedWorkflows.slice(0, 3)).map(renderCompletedWorkflowRow)}
          </Card>
          {completedWorkflows.length > 3 && (
            <Button
              variant="ghost"
              className="w-full mt-2 text-sm text-muted-foreground hover:text-foreground"
              onClick={() => setShowAllCompleted(!showAllCompleted)}
            >
              {showAllCompleted 
                ? "Show Less" 
                : `View All (${completedWorkflows.length - 3} more)`}
            </Button>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Abandon Workflow?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to abandon "{workflowToDelete?.workflow_title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Abandon
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
